import { GymNative, isNativeAndroid, nativeAuthToken } from "./native.js";

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const headers = new Headers(options?.headers);
  const token = await nativeAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (options?.body !== undefined && options.body !== null && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(apiUrl(path), {
    credentials: isNativeAndroid ? "omit" : "same-origin",
    ...options,
    headers
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText })) as { error?: string };
    throw new ApiError(body.error ?? "Request failed", response.status);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

function apiUrl(path: string): string {
  const configured = import.meta.env.VITE_API_ORIGIN?.replace(/\/$/, "") ?? "";
  const origin = configured || (isNativeAndroid ? "https://gym.alexanderqho.com" : "");
  return origin && path.startsWith("/") ? `${origin}${path}` : path;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(
    path,
    body === undefined ? { method: "POST" } : { method: "POST", body: JSON.stringify(body) }
  ),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  login: async (password: string) => {
    if (!isNativeAndroid) return request<{ authenticated: boolean }>("/api/auth/login", { method: "POST", body: JSON.stringify({ password }) });
    const result = await request<{ authenticated: boolean; accessToken: string }>("/api/auth/mobile/login", {
      method: "POST",
      body: JSON.stringify({ password, deviceName: "Samsung Galaxy A33 5G" })
    });
    await GymNative.setAuthToken({ token: result.accessToken });
    return { authenticated: result.authenticated };
  },
  session: () => request<{ authenticated: boolean }>("/api/auth/session"),
  logout: async () => {
    try { await request<{ authenticated: boolean }>("/api/auth/logout", { method: "POST" }); }
    finally {
      if (isNativeAndroid) {
        await GymNative.clearActiveWorkout().catch(() => undefined);
        await GymNative.clearAuthToken();
      }
    }
  }
};
